import * as _ from 'lodash';
import { map, pipe, uniq } from 'remeda';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Receivable, Util } from '@finley-technologies/finley-types';

import { LpToCfEntityGrouping } from './types';
import {
  AssignmentReceivableGQLType,
  BaseFieldGQLType,
  EligibilityStatusContainerGQLType,
  LoanProductGQLType,
  PostProcessedFieldsGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { CellType } from 'handsontable/cellTypes';
import { DetailedSettings } from 'handsontable/plugins/hiddenColumns';
import { ColumnSettings } from 'handsontable/settings';

export const AssignmentEditTableKeyIterable = [
  'proposed',
  'unassigned',
  'previouslyAssigned',
] as const;
export type AssignmentEditTableKeyType = typeof AssignmentEditTableKeyIterable[number];

export const AssignmentEditTableKey = {
  proposed: 'proposed',
  unassigned: 'unassigned',
  previouslyAssigned: 'previouslyAssigned',
} as const;
// eslint-disable-next-line no-redeclare
export type AssignmentEditTableKey = AssignmentEditTableKeyType;

export const assignmentTableContentWidth = 1250;
export const assignmentTablePaddingSingle = 15;
export const assignmentTablePadding = `0 ${assignmentTablePaddingSingle}px ${assignmentTablePaddingSingle}px ${assignmentTablePaddingSingle}px`;

// Handsontable custom renderers //
export const disabledCheckboxHandsontableRenderer = (
  _hotInstance: any,
  td: HTMLTableCellElement,
) => {
  // eslint-disable-next-line no-param-reassign
  td.outerHTML = `<td class="assignment-cell"><input class="htCheckboxRendererInput" type="checkbox" disabled style="cursor: not-allowed"></td>`;
  return td;
};

export const alwaysHiddenColumnFields = [
  // used for determining the available cfids for proposed owner selection for receivable
  'assignableCreditFacilityIds',
  // used to map to internal finley receivable for preview & assignment
  'finleyReceivableId',
];

export const assignmentEditTableKeyToTitleMap: {
  [key in AssignmentEditTableKey]: string;
} = {
  proposed: 'Preview receivables to assign',
  unassigned: 'Preview receivables to leave on balance sheet',
  previouslyAssigned: 'Previously assigned receivables',
};

export const unassignedReceivableOwnerLabel = 'Balance sheet';

export const parseCompleteCurrentMasterLoanTapeSetForLoanProductIds = (
  loanProductsForOrganization: LoanProductGQLType[],
) => {
  const loanProductIdsForOrganization = _.map(
    loanProductsForOrganization,
    ({ id }) => id,
  );
  const masterLoanTapesMetadata = _.compact(
    _.map(loanProductsForOrganization, ({ currentMasterLoanTape }) => {
      if (!currentMasterLoanTape) {
        return undefined;
      }
      return currentMasterLoanTape;
    }),
  );

  const uniqueFileVersions = pipe(
    masterLoanTapesMetadata,
    map(({ fileVersion }) => fileVersion),
    uniq(),
  );
  const uniqueReportingDates = pipe(
    masterLoanTapesMetadata,
    map(({ reportingDate }) => reportingDate),
    uniq(),
  );
  // masterLoanTapeExistsForEveryLoanProductId will fail if any masterLoanTapeMetadata was undefined and compacted
  const masterLoanTapeExistsForEveryLoanProductId =
    loanProductIdsForOrganization.length === masterLoanTapesMetadata.length;

  if (
    uniqueFileVersions.length !== 1 ||
    uniqueReportingDates.length !== 1 ||
    !masterLoanTapeExistsForEveryLoanProductId
  ) {
    return undefined;
  }

  return {
    fileVersion: uniqueFileVersions[0],
    reportingDate: uniqueReportingDates[0],
    masterLoanTapeIds: _.map(masterLoanTapesMetadata, ({ id }) => id),
  };
};

const getBorrowerEntityNameForAssignedReceivable = (
  receivable: AssignmentReceivableGQLType,
  creditFacilitiesForBorrowerMap: {
    [key: string]: { id: string; borrowerEntityName: string };
  },
) => {
  if (!receivable) {
    return undefined;
  }
  finleyAssert(
    receivable.receivableResponseTapeProperties,
    'receivable with assignment or preview must contain receivableResponseTapeProperties',
  );
  const creditFacilityId =
    receivable.receivableResponseTapeProperties.creditFacilityId;

  const borrowerEntityName = creditFacilitiesForBorrowerMap[creditFacilityId]
    ? creditFacilitiesForBorrowerMap[creditFacilityId].borrowerEntityName
    : '';
  return borrowerEntityName;
};

const getTotalExposureForReceivable = (
  receivable: AssignmentReceivableGQLType,
) => {
  if (!receivable) {
    return 0;
  }
  const requiredIntermediateFieldsForOneFacility = _.first(
    _.values(receivable.requiredIntermediateFields),
  );
  finleyAssert(
    requiredIntermediateFieldsForOneFacility,
    'receivable must have at least one set of requiredIntermediateFields',
  );
  return requiredIntermediateFieldsForOneFacility.requiredIntermediateFields
    .exposure;
};

const getBorrowingBaseAmountForReceivable = (
  receivable: AssignmentReceivableGQLType,
) => {
  if (!receivable) {
    return 0;
  }
  finleyAssert(
    receivable.receivableResponseTapeProperties,
    'receivable with assignment or preview must contain receivableResponseTapeProperties',
  );
  return receivable.receivableResponseTapeProperties.borrowingBaseFields
    .borrowingBaseAmount;
};

export const groupAssignmentReceivables = (
  assignmentReceivables: AssignmentReceivableGQLType[],
) => {
  const toAssignReceivables: AssignmentReceivableGQLType[] = [];
  const unassignedReceivables: AssignmentReceivableGQLType[] = [];
  const previouslyAssignedReceivables: AssignmentReceivableGQLType[] = [];

  _.forEach(assignmentReceivables, (receivable) => {
    if (
      receivable.isAssignable &&
      !!receivable.receivableResponseTapeProperties
    ) {
      toAssignReceivables.push(receivable);
    }

    if (
      receivable.isAssignable &&
      !receivable.receivableResponseTapeProperties
    ) {
      unassignedReceivables.push(receivable);
    }

    if (
      !receivable.isAssignable &&
      !!receivable.receivableResponseTapeProperties
    ) {
      previouslyAssignedReceivables.push(receivable);
    }
  });

  return {
    toAssignReceivables,
    unassignedReceivables,
    previouslyAssignedReceivables,
  };
};

export const groupReceivablesByCreditFacilityId = (
  receivables: AssignmentReceivableGQLType[],
) => {
  return _.groupBy(receivables, (receivable) => {
    finleyAssert(
      receivable.receivableResponseTapeProperties,
      'receivable with assignment or preview must contain receivableResponseTapeProperties',
    );
    return receivable.receivableResponseTapeProperties.creditFacilityId;
  });
};

const getIneligibleCriteriaKeys = (
  eligibilityStatuses: EligibilityStatusContainerGQLType[],
) => {
  return eligibilityStatuses
    .filter(({ eligibilityStatus }) => eligibilityStatus === 'INELIGIBLE')
    .map(({ eligibilityCriteriaKey }) => eligibilityCriteriaKey);
};

export const parseAssignmentReceivablesToTableData = (
  receivables: AssignmentReceivableGQLType[],
  creditFacilitiesForBorrowerMap: {
    [key: string]: { id: string; borrowerEntityName: string };
  },
) => {
  const receivablesTableData = _.map(receivables, (receivable) => {
    const baseFields: { [key: string]: Util.Primitive } = {};
    _.forEach(receivable.baseFields, (baseField) => {
      baseFields[baseField.key] = baseField.value;
    });

    // return table data for receivable
    return {
      selected: false, // placeholder for checkbox that defaults to unchecked
      externalReceivableId: receivable.externalReceivableId,
      exposure: getTotalExposureForReceivable(receivable),
      borrowingBaseAmount: receivable.receivableResponseTapeProperties
        ? getBorrowingBaseAmountForReceivable(receivable)
        : 0,
      owner: receivable.receivableResponseTapeProperties
        ? getBorrowerEntityNameForAssignedReceivable(
            receivable,
            creditFacilitiesForBorrowerMap,
          )
        : unassignedReceivableOwnerLabel,
      assignableCreditFacilityIds: receivable.assignableCreditFacilityIds,
      finleyReceivableId: receivable.finleyReceivableId,
      ...baseFields,
    };
  });

  return receivablesTableData;
};

type ReceivablesTableComments = {
  row: number;
  col: number;
  comment: {
    value: string;
    readOnly: boolean;
  };
};

export const parseAssignmentReceivablesToTableComments = (
  receivables: AssignmentReceivableGQLType[],
  commentsColumnIndex: number = 3,
) => {
  const receivablesTableComments: {
    eligibilityComments: ReceivablesTableComments[];
    concentrationComments: ReceivablesTableComments[];
  } = { eligibilityComments: [], concentrationComments: [] };

  _.forEach(receivables, (receivable, index) => {
    const ineligibleCriteriaKeys = getIneligibleCriteriaKeys(
      receivable.receivableResponseTapeProperties?.eligibilityStatuses || [],
    );

    // generate comments tooltip for receivable if applicable
    if (
      receivable.receivableResponseTapeProperties &&
      (receivable.receivableResponseTapeProperties.concentrationLimitReasons
        .length ||
        ineligibleCriteriaKeys.length)
    ) {
      const { receivableResponseTapeProperties } = receivable;

      // show concentration reasons only if there are no eligibility reasons
      if (ineligibleCriteriaKeys.length) {
        const formattedReasons = _.map(ineligibleCriteriaKeys, (reason) =>
          commonUtils.toSentenceCase(reason),
        );
        receivablesTableComments.eligibilityComments.push({
          row: index,
          col: commentsColumnIndex,
          comment: {
            value: `Ineligible: ${_.join(formattedReasons, ', ')}`,
            readOnly: true,
          },
        });
      } else if (
        receivableResponseTapeProperties.concentrationLimitReasons.length
      ) {
        const formattedReasons = _.map(
          receivableResponseTapeProperties.concentrationLimitReasons,
          ({ propertyKey }) => commonUtils.toSentenceCase(propertyKey),
        );
        receivablesTableComments.concentrationComments.push({
          row: index,
          col: commentsColumnIndex,
          comment: {
            value: `Concentration reductions: ${_.join(
              formattedReasons,
              ', ',
            )}`,
            readOnly: true,
          },
        });
      }
    }
  });

  return receivablesTableComments;
};

export const parseAssignmentReceivablesFromJson = (
  test?: string,
): AssignmentReceivableGQLType[] => {
  if (!test) {
    return [];
  }
  const assignmentReceivables = JSON.parse(test);

  return assignmentReceivables;
};

export const formatSignaledAssignmentsFromReceivablesHotData = ({
  receivablesHotData,
  receivableIdColumnIndex,
  receivableOwnerColumnIndex,
  borrowerEntityNameForCreditFacilityMap,
}: {
  receivablesHotData: [string | number | string[]][];
  receivableIdColumnIndex: number;
  receivableOwnerColumnIndex: number;
  borrowerEntityNameForCreditFacilityMap: {
    [key: string]: {
      id: string;
      borrowerEntityName: string;
    };
  };
}) => {
  const signaledAssignments = _.compact(
    _.map(receivablesHotData, (receivable) => {
      finleyAssert(
        receivable[receivableOwnerColumnIndex] &&
          receivable[receivableIdColumnIndex],
        `Handsontable receivable data must include receivable owner at column index ${receivableOwnerColumnIndex} and receivable id at column index ${receivableIdColumnIndex}`,
      );

      const receivableBorrowerEntityName =
        receivable[receivableOwnerColumnIndex];
      const receivableId = receivable[receivableIdColumnIndex];

      finleyAssert(
        typeof receivableBorrowerEntityName === 'string' &&
          typeof receivableId === 'string',
        'Handsontable receivable id and borrowerEntityName must be strings',
      );

      const assignedCreditFacility =
        borrowerEntityNameForCreditFacilityMap[receivableBorrowerEntityName];

      // if !assignedCreditFacility, do not create signaledAssignment to leave on balance sheet
      if (!assignedCreditFacility) {
        return undefined;
      }
      return {
        finleyReceivableId: receivableId,
        creditFacilityId: assignedCreditFacility.id,
      };
    }),
  );

  return signaledAssignments;
};

/**
 * Returns ColumnSettings for required and basefield columns
 * @param isAssigned whether the table houses already-assigned receivables
 */
export const getBaseFieldColumnSettings = (
  assignmentReceivables: AssignmentReceivableGQLType[],
  isAssigned: boolean,
): {
  requiredColumns: ColumnSettings[];
  baseFieldsColumns: ColumnSettings[];
} => {
  const primitiveToColumnTypeMap: { [key: string]: CellType } = {
    string: 'text',
    number: 'numeric',
  };
  const sharedRequiredColumns = [
    {
      data: 'externalReceivableId',
    },
    {
      data: 'exposure',
      type: 'numeric',
      numericFormat: {
        pattern: '$0,0.00',
        culture: 'en-US',
      },
    },
    {
      data: 'borrowingBaseAmount',
      type: 'numeric',
      numericFormat: {
        pattern: '$0,0.00',
        culture: 'en-US',
      },
    },
  ];
  const requiredColumns = [
    isAssigned
      ? {
          data: 'selected',
          renderer: 'disabledCheckbox',
          multiColumnSorting: {
            headerAction: false,
          },
        }
      : {
          data: 'selected',
          type: 'checkbox',
          readOnly: false,
          multiColumnSorting: {
            headerAction: false,
          },
        },
    ...sharedRequiredColumns,
  ];

  if (_.isEmpty(assignmentReceivables)) {
    return {
      requiredColumns,
      baseFieldsColumns: [],
    };
  }

  const baseFieldsColumns = _.map(
    assignmentReceivables[0].baseFields,
    (baseField) => {
      return {
        data: baseField.key,
        type: primitiveToColumnTypeMap[typeof baseField.value],
      };
    },
  );
  return {
    requiredColumns,
    baseFieldsColumns,
  };
};

/**
 * Returns an array of required assignment column headers
 * @param isAssigned whether the table houses already-assigned receivables
 */
export const getRequiredAssignmentTableColumnHeaders = ({
  isAssigned,
}: {
  isAssigned: boolean;
}) => {
  const requiredColumnHeaders = [
    '',
    'RECEIVABLE ID',
    'EXPOSURE',
    'BORROWING BASE AMOUNT',
  ];

  return isAssigned
    ? [...requiredColumnHeaders, 'OWNER']
    : [...requiredColumnHeaders, 'PROPOSED OWNER']; // includes first checkbox column with empty header
};

export const getSharedAssignableCfIds = (
  dataByTable: {
    data: string[][];
    selectedColIndex: number;
    assignableCfIdsColIndex: number;
  }[],
) => {
  const assignableCfIdsForEachTable = _.flatMap(dataByTable, (dataForTable) => {
    const { data, selectedColIndex, assignableCfIdsColIndex } = dataForTable;

    const assignableCfIdsForTable =
      _.map(
        _.filter(data, (row) => !!row[selectedColIndex]),
        (selectedRow) => selectedRow[assignableCfIdsColIndex],
      ) || [];

    return assignableCfIdsForTable;
  });

  return _.intersection(...assignableCfIdsForEachTable);
};

// @jendevelops: Deprecated
export const formatMultiSelectCfidOptions = (
  sharedAssignableCfIds: string[],
  creditFacilitiesForBorrowerMap: {
    [key: string]: { id: string; borrowerEntityName: string };
  },
): { value: string; label: string }[] => {
  const assignableCfidOptions = _.map(sharedAssignableCfIds, (cfid) => {
    const borrowerEntityName =
      creditFacilitiesForBorrowerMap[cfid].borrowerEntityName;
    return {
      value: borrowerEntityName,
      label: borrowerEntityName,
    };
  });
  return [
    ...assignableCfidOptions,
    {
      value: unassignedReceivableOwnerLabel,
      label: unassignedReceivableOwnerLabel,
    },
  ];
};

export const getNumberOfSelectedReceivables = (
  dataByTable: {
    data: string[][];
    selectedColIndex: number;
  }[],
) => {
  const numberOfSelectedReceivables = _.sumBy(dataByTable, (dataForTable) => {
    const { data, selectedColIndex } = dataForTable;

    return _.sumBy(data, (row) => {
      if (row[selectedColIndex]) {
        return 1;
      }
      return 0;
    });
  });
  return numberOfSelectedReceivables;
};

export const formatNewDataAtPropForSelectedReceivables = ({
  data,
  selectedColIndex,
  prop,
  newValue,
}: {
  data: any[][];
  selectedColIndex: number;
  prop: string;
  newValue: string | undefined;
}) => {
  const selectedRowIndices: number[] = [];
  _.forEach(data, (row, rowIndex) => {
    if (row[selectedColIndex]) {
      selectedRowIndices.push(rowIndex);
    }
  });

  return _.map(selectedRowIndices, (selectedRowIndex) => {
    return [selectedRowIndex, prop, newValue];
  });
};

/**
 * gets the handsontable physical column indices for base fields
 * used in eligibility criteria calculations
 */
export const getEligibilityBaseFieldsColumnIndices = ({
  assignmentReceivable,
  eligibilityBaseFields,
}: {
  assignmentReceivable?: AssignmentReceivableGQLType;
  eligibilityBaseFields: string[];
}) => {
  if (!assignmentReceivable) {
    return undefined;
  }
  const requiredColumnsLength = getRequiredAssignmentTableColumnHeaders({
    isAssigned: false, // this does not impact length of required columns
  }).length;
  const eligibilityBaseFieldsIndices = _.compact(
    _.map(assignmentReceivable.baseFields, (baseField, idx) => {
      if (_.includes(eligibilityBaseFields, baseField.key)) {
        return _.toString(
          idx + requiredColumnsLength + alwaysHiddenColumnFields.length,
        );
      }
      return undefined;
    }),
  );

  return eligibilityBaseFieldsIndices;
};

export const getInitialHiddenColumns = ({
  assignmentReceivable,
  tableIsAssigned,
}: {
  assignmentReceivable?: AssignmentReceivableGQLType;
  tableIsAssigned: boolean;
}): DetailedSettings => {
  const requiredColumnsLength = getRequiredAssignmentTableColumnHeaders({
    isAssigned: tableIsAssigned,
  }).length;

  return {
    columns: _.range(
      requiredColumnsLength,
      assignmentReceivable
        ? assignmentReceivable.baseFields.length +
            requiredColumnsLength +
            alwaysHiddenColumnFields.length
        : requiredColumnsLength,
    ),
    indicators: false,
  };
};

const isHiddenColumnsSetting = (test: any): test is DetailedSettings => {
  return (
    test &&
    'columns' in test &&
    !_.isEmpty(test.columns) &&
    _.every(
      test.columns,
      (columnIndexStr) => !_.isNaN(Number(columnIndexStr)),
    ) &&
    'indicators' in test &&
    typeof test.indicators === 'boolean'
  );
};

export const getHiddenColumnsFromLocalStorage = (
  tableKey: AssignmentEditTableKey,
) => {
  const localStorageHiddenColumnsStr = localStorage.getItem(
    `${tableKey}_hiddenColumns`,
  );
  const localStorageHiddenColumnsObj = localStorageHiddenColumnsStr
    ? JSON.parse(localStorageHiddenColumnsStr)
    : undefined;

  return isHiddenColumnsSetting(localStorageHiddenColumnsObj)
    ? localStorageHiddenColumnsObj
    : undefined;
};

export const getColumnSelectOptions = (baseFields: BaseFieldGQLType[]) => {
  const requiredColumnHeaders = getRequiredAssignmentTableColumnHeaders({
    isAssigned: false,
  });
  const allBaseFieldKeysSelectOptions = _.map(baseFields, ({ key }, index) => {
    return {
      label: commonUtils.toTitleCase(key),
      value: `${
        index + requiredColumnHeaders.length + alwaysHiddenColumnFields.length
      }`, // columnIndex includes required columns and alwaysHiddenColumns
    };
  });

  return allBaseFieldKeysSelectOptions;
};

// @dani refactor after moving AssignmentReceivable type to finley-types
// Remove AssignmentReceivableGQLType and share type on FE and BE
// Remove flatten translation in assignment receivables resolvers
export const assignmentReceivablesToBorrowingBaseReceivables = ({
  assignmentReceivables,
  creditFacilityId,
}: {
  assignmentReceivables: AssignmentReceivableGQLType[];
  creditFacilityId: string;
}): Receivable.CreditFacility.BorrowingBaseReceivable[] => {
  const borrowingBaseReceivables = assignmentReceivables.map(
    (assignmentReceivable) => {
      const {
        finleyReceivableId,
        externalReceivableId,
        loanProductId,
        baseFields,
        dynamicIntermediateFields,
        requiredIntermediateFields,
        postProcessedFields,
        receivableResponseTapeProperties,
      } = assignmentReceivable;

      finleyAssert(
        receivableResponseTapeProperties,
        `receivableResponseTapeProperties must exist for assigned or proposed assignmentReceivable. finleyReceivableId: ${finleyReceivableId}`,
      );

      const {
        borrowingBaseFields,
        concentrationLimitFields,
        concentrationLimitReasons,
        concentrationLimitWaivers,
        eligibilityFields,
        eligibilityStatuses,
        valuationFields,
        assignmentFields,
      } = receivableResponseTapeProperties;

      const translateBaseFields = (gqlBaseFields: BaseFieldGQLType[]) => {
        const translatedBaseFields: Receivable.ReceivableBaseFields = {};
        _.forEach(gqlBaseFields, (bf) => {
          translatedBaseFields[bf.key] = bf.value;
        });
        return translatedBaseFields;
      };

      // translate gql maybe back to null
      const translatePostProcessedFields = (
        _postProcessedFields: PostProcessedFieldsGQLType,
      ): Receivable.CreditFacility.PostProcessedFields => {
        const {
          preassignmentCreditFacilityId,
          performingExposure,
        } = _postProcessedFields;

        return {
          ..._postProcessedFields,
          preassignmentCreditFacilityId: preassignmentCreditFacilityId ?? null,
          performingExposure: performingExposure ?? undefined, // @required-fields - remove once propagated
        };
      };

      const requiredIntermediateFieldsForCreditFacility = _.find(
        requiredIntermediateFields,
        (requiredIntermediateFieldsByCreditFacility) =>
          requiredIntermediateFieldsByCreditFacility.creditFacilityId ===
          creditFacilityId,
      );

      const dynamicIntermediateFieldsForCreditFacility = _.find(
        dynamicIntermediateFields,
        (dynamicIntermediateFieldsByCreditFacility) =>
          dynamicIntermediateFieldsByCreditFacility.creditFacilityId ===
          creditFacilityId,
      );

      const postProcessedFieldsForCreditFacility = _.find(
        postProcessedFields,
        (postProcessedFieldsByCreditFacility) =>
          postProcessedFieldsByCreditFacility.creditFacilityId ===
          creditFacilityId,
      );

      finleyAssert(
        requiredIntermediateFieldsForCreditFacility &&
          dynamicIntermediateFieldsForCreditFacility &&
          postProcessedFieldsForCreditFacility,
        `requiredIntermediateFields, dynamicIntermediateFields, and postProcessedFields must exist for credit facility id ${creditFacilityId}`,
      );

      const borrowingBaseReceivable: Receivable.CreditFacility.BorrowingBaseReceivable = {
        loanProductId,
        finleyReceivableId,
        externalReceivableId,
        baseFields: translateBaseFields(baseFields),
        requiredIntermediateFields:
          requiredIntermediateFieldsForCreditFacility.requiredIntermediateFields,
        dynamicIntermediateFields: translateBaseFields(
          dynamicIntermediateFieldsForCreditFacility.dynamicIntermediateFields,
        ),
        postProcessedFields: translatePostProcessedFields(
          postProcessedFieldsForCreditFacility.postProcessedFields,
        ),
        borrowingBaseFields,
        concentrationLimitReasons,
        concentrationLimitFields,
        concentrationLimitWaivers,
        eligibilityStatuses,
        eligibilityFields,
        valuationFields,
        assignmentFields: assignmentFields || null,
      };

      return borrowingBaseReceivable;
    },
  );

  return borrowingBaseReceivables;
};

export const getLpToCfGroupingName = (lpToCfGrouping: LpToCfEntityGrouping) => {
  const { creditFacilities, loanProducts } = lpToCfGrouping;

  return `${loanProducts
    .map((lp) => lp.name)
    .join(', ')} & ${creditFacilities
    .map((facility) => facility.name)
    .join(', ')}`;
};
